/**
 * `togglePanel` lives in its own module, same reasoning as frames.js's
 * `frameReceived`: SidebarNav is part of the persistent app shell (rendered
 * on every authenticated page) and only needs to flip the panel open/closed
 * — it must not drag the AI chat API client (and everything that pulls in)
 * into its import graph just for that. `aiChatSlice` handles this action via
 * `extraReducers`, exactly like it already does `frameReceived`. The
 * entry points' visibility rule lives here for the same reason.
 */
import { createAction } from '@reduxjs/toolkit'
import { isSetupPage } from './setupPages'

export const togglePanel = createAction('aiChat/togglePanel')

/**
 * Whether a `GET /api/ai/status/` body leaves the plan's assistant in. False
 * only when the plan has no assistant; `reason: 'budget_exceeded'` (FE-A9)
 * also reports `enabled: false`, but that is a paused state the viewer must
 * be able to open and read (PanelStates' BudgetState). An unknown status
 * (still loading) counts as in.
 *
 * @param {Object|null|undefined} status
 * @returns {boolean}
 */
export const planIncludesAssistant = (status) =>
  !(status && status.enabled === false && status.reason !== 'budget_exceeded')

/**
 * Whether the assistant's entry points show: hidden only when the plan has no
 * assistant (`planIncludesAssistant`).
 *
 * @param {Object} state
 * @returns {boolean}
 */
export const selectAssistantVisible = (state) => planIncludesAssistant(state.aiChat?.status)

/**
 * Free setup help is available to this viewer (`status.setup_chat`, backend
 * `setup_chat.availability`): an administrator of a young workspace, on any plan.
 *
 * @param {Object} state
 * @returns {boolean}
 */
export const selectSetupHelpAvailable = (state) => state.aiChat?.status?.setup_chat?.available === true

/**
 * The workspace had free setup help and it is over (`status.setup_chat.ended`,
 * backend `Availability.ended`).
 *
 * @param {Object} state
 * @returns {boolean}
 */
export const selectSetupHelpEnded = (state) => state.aiChat?.status?.setup_chat?.ended === true

/**
 * What the panel can do on `page`. `setupMode`: the conversation is (or will
 * be) setup-scoped - a help button asked, the open session is one, or the
 * panel is on a setup page with no other conversation open - and either free
 * setup help or the plan's assistant answers it. `isComped`: setup mode on
 * free setup help (the last send was not billed to the plan); the plan and
 * budget states then do not apply. `planHasAssistant`: the status has loaded
 * and the plan includes the assistant, so its history, starters and
 * suggestions answer.
 *
 * @param {Object} state
 * @param {string} [page] - `pageContext.page`
 * @returns {{setupMode: boolean, isComped: boolean, overBudget: boolean, unavailable: boolean, planHasAssistant: boolean}}
 */
export function selectPanelAvailability(state, page) {
  const { status, setupRequest, sessionPurpose, billing } = state.aiChat
  const planHasAssistant = Boolean(status) && selectAssistantVisible(state)
  const isSetupHelpFree = selectSetupHelpAvailable(state) && billing !== 'plan'
  const asksForSetup = Boolean(setupRequest) || sessionPurpose === 'onboarding' || (!sessionPurpose && isSetupPage(page))
  const setupMode = sessionPurpose !== 'chat' && asksForSetup && (isSetupHelpFree || planHasAssistant)
  const isComped = setupMode && isSetupHelpFree
  const overBudget = !isComped && (status?.reason === 'budget_exceeded' || status?.blocked === 'budget_exceeded')
  const unavailable = !isComped && (Boolean(status && status.enabled === false) || overBudget)
  return { setupMode, isComped, overBudget, unavailable, planHasAssistant }
}
